import { request } from "../../../services/api.ts"
import { type BaseApiResponse } from "../../../types/api.ts"
import { buildFilterParams } from "../redux/globalFilters.ts"
import type {
  GlobalFilters,
  NbaItem,
  PortfolioBundle,
  PortfolioWindow
} from "../redux/portfolio.types.ts"
import type {
  ResolvedWidget,
  WidgetCatalog,
  WidgetInstanceV4,
  WidgetsResponse
} from "../redux/widgets.types.ts"
import type { DashboardStats } from "../types/types.ts"

const BASE_URL = "/user/users"

const PATHS = {
  getStats: `${BASE_URL}/home/`,
  portfolio: "/dashboard/portfolio/",
  widgets: "/dashboard/widgets/",
  resolve: "/dashboard/widgets/resolve/",
  catalog: "/dashboard/catalog/",
  nba: "/dashboard/nba/",
  exportPdf: "/dashboard/export.pdf",
  exportXlsx: "/dashboard/export.xlsx"
}

export const getDashboardStats = () =>
  request.api.get<DashboardStats, BaseApiResponse<void>>(PATHS.getStats)

const buildWindowParams = (window: PortfolioWindow) => {
  if (window.preset === "custom") {
    return { window: "custom", start: window.start, end: window.end }
  }
  return { window: window.preset }
}

export const getDashboardPortfolio = (
  window: PortfolioWindow,
  filters?: GlobalFilters
) =>
  request.api.get<PortfolioBundle>(PATHS.portfolio, {
    ...buildWindowParams(window),
    ...buildFilterParams(filters)
  })

export const getDashboardWidgets = (
  window: PortfolioWindow,
  filters?: GlobalFilters
) =>
  request.api.get<WidgetsResponse>(PATHS.widgets, {
    ...buildWindowParams(window),
    ...buildFilterParams(filters)
  })

export const resolveDashboardWidget = (
  instance: Pick<WidgetInstanceV4, "type" | "dataSource" | "config">,
  window: PortfolioWindow,
  filters?: GlobalFilters
) =>
  request.api.post<ResolvedWidget, BaseApiResponse<void>>(
    PATHS.resolve,
    {
      type: instance.type,
      dataSource: instance.dataSource,
      config: instance.config
    },
    {
      params: {
        ...buildWindowParams(window),
        ...buildFilterParams(filters)
      }
    }
  )

// `role` scopes the catalog to a target role instead of the caller — the
// template editor authors for an audience, not for itself.
export const getDashboardCatalog = (role?: number | null) =>
  request.api.get<WidgetCatalog>(PATHS.catalog, role != null ? { role } : undefined)

export const getDashboardNba = () =>
  request.api.get<{ items: NbaItem[]; total_unfiltered: number }>(PATHS.nba)

export const exportDashboardPdf = (
  window: PortfolioWindow,
  filters?: GlobalFilters
) =>
  request.api.get<Blob, BaseApiResponse<void>>(
    PATHS.exportPdf,
    { ...buildWindowParams(window), ...buildFilterParams(filters) },
    { responseType: "blob" }
  )

export const exportDashboardXlsx = (
  window: PortfolioWindow,
  filters?: GlobalFilters
) =>
  request.api.get<Blob, BaseApiResponse<void>>(
    PATHS.exportXlsx,
    { ...buildWindowParams(window), ...buildFilterParams(filters) },
    { responseType: "blob" }
  )
